import { useMemo, useRef, useState } from 'react';
import { Center, Spinner } from 'src/components/atoms';
import {
  BarHorizontal,
  HeroMetric,
  Legends,
  Table,
} from 'src/components/molecules';
import {
  GlobalFilter,
  LocalFilter,
  PaginatedTable,
  SelectedLocalFilters,
} from 'src/components/organisms';
import { GraphPanel } from 'src/components/organisms/GraphPanel';
import { useElementDimensions } from 'src/hooks';
import { useOrganisationInsightsData } from 'src/hooks/insights/useOrganisationInsightsData';
import { InsightsDashboard, Organisation } from 'src/types';
import { CHART_IDS } from 'src/utils/analytics';
import { InsightsDisclaimer } from '../EthicalHealthDataview/EthicalHealthDataview.styles';
import { useFormatPdfFilter } from '../EthicalHealthDataview/hooks/useFormatPdfFilter';
import { BarStackedContent, DonutAndBarContent } from './components';
import {
  ClickableTitle,
  GraphContainer,
  GraphTableContainer,
  HeroMetricContainer,
  OperationalHealthDashboardStyled,
  PageTitle,
} from './EthicsOperationsDataview.styles';
import { labelExtractor } from './helpers';
import {
  useCasesOverTime,
  useHeroMetrics,
  useInsightsGlobalFilters,
  useIssueCategoriesByIntakeMethod,
  useOperationalHealthDownloads,
  useSubstantiationTotals,
} from './hooks';

const defaultLocalFilter: SelectedLocalFilters = {
  options: {},
  saveFunction: () => {},
};

interface Props {
  organisation: Organisation | null;
}

export const EthicsOperationsDataview = ({ organisation }: Props) => {
  const [localFiltersOpen, setLocalFiltersOpen] = useState(false);
  const [selectedLocalFilters, setSelectedLocalFilters] =
    useState<SelectedLocalFilters>(defaultLocalFilter);
  const [filteredChartId, setFilteredChartId] = useState<
    CHART_IDS | undefined
  >();

  const barStackedRef = useRef<HTMLDivElement | null>(null);
  const barHorizontalRef = useRef<HTMLDivElement | null>(null);
  const donutAndBarRef = useRef<HTMLDivElement | null>(null);

  const {
    organisationSites,
    organisationCountries,
    organisationCaseCountries,
    organisationCaseRegions,
    organisationDepartments,
    organisationCategories,
    oldestReportSubmittedDate,
  } = useOrganisationInsightsData();

  const chartOrganisationData = useMemo(
    () => ({
      sites: organisationSites,
      countries: organisationCountries,
      departments: organisationDepartments,
      oldestReportSubmittedAtDate: oldestReportSubmittedDate,
      categories: organisationCategories,
    }),
    [
      organisationSites,
      organisationCountries,
      organisationDepartments,
      oldestReportSubmittedDate,
      organisationCategories,
    ],
  );

  const {
    globalFilterOpen,
    closeGlobalFilter,
    globalFilterProps,
    locationTitle,
    onLocationTitleClick,
    selectedSites: globalFilterSites,
    selectedCountries: globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    titleHoverDisplay,
    timeframeTitle,
    selectedTimeframe: globalFilterTimeframe,
    onTimeTitleClick,
  } = useInsightsGlobalFilters({
    organisationSites,
    organisationCountries,
    organisationCaseCountries,
    organisationCaseRegions,
    oldestReportSubmittedDate,
    dashboard: InsightsDashboard.OPERATIONAL,
  });

  const { data: heroMetricData, isLoading } = useHeroMetrics({
    globalFilterSites,
    globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    globalFilterTimeframe,
  });

  const {
    barStackedData,
    timeToResolutionLegend,
    isLoading: isBarStackedDataLoading,
    onStackedBarLegendClick,
    localFilterOptions: stackedBarLocalFilterOptions,
    saveLocalFilters: saveStackedBarLocalFilterOptions,
    casesOverTime,
    tableColumns: barStackedTableColumns,
    tableData: barStackedTableData,
    handleColumnSort: handleBarStackedColumnSort,
    sortedColumn: barStackedSortedColumn,
    pdfIsDownloading: barStackedChartPdfIsDownloading,
    setPdfIsDownloading: setBarStackedChartPdfIsDownloading,
    localLocationFilter: stackedBarLocalLocationFilter,
    resetLocalFilters: resetStackedBarLocalFilterOptions,
  } = useCasesOverTime({
    globalFilterSites,
    globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    globalFilterTimeframe,
    organisationData: chartOrganisationData,
  });

  const {
    xTitle: barHorizontalXTitle,
    yTitle: barHorizontalYTitle,
    data: barHorizontalData,
    substantiationLegend,
    onHover: onSubstantiationBarHover,
    substantiationHoverBarColor,
    localFilterOptions: substantiationChartLocalFilters,
    saveLocalFilters: saveSubstantiationChartLocalFilters,
    tableColumns: barHorizontalTableColumns,
    tableData: barHorizontalTableData,
    handleColumnSort: handlebarHorizontalColumnSort,
    sortedColumn: barHorizontalSortedColumn,
    pdfIsDownloading: substantiationChartPdfIsDownloading,
    setPdfIsDownloading: setSubstantiationChartPdfIsDownloading,
    localLocationFilter: substantiationChartLocalLocationFilter,
    resetLocalFilters: resetSubstantiationChartLocalFilters,
  } = useSubstantiationTotals({
    globalFilterSites,
    globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    globalFilterTimeframe,
    organisationData: chartOrganisationData,
  });

  const {
    donutData,
    onDonutClick,
    donutLegend,
    centerLabel,
    barData,
    xTitle: barXTitle,
    yTitle: barYTitle,
    barColor,
    xAxisMax,
    localFilterOptions: donutAndBarHorizontalBarFilters,
    saveLocalFilters: saveDonutAndBarHorizontalBarFilters,
    dataIsLoading: donutAndBarHorizontalBarLoading,
    categoriesByIntakeMethod,
    onLegendClick: onDonutLegendClick,
    selectedSegment: selectedDonutSegment,
    tableColumns: donutAndBarTableColumns,
    tableData: donutAndBarTableData,
    handleColumnSort: handleDonutAndBarColumnSort,
    sortedColumn: donutAndBarSortedColumn,
    categoryAxisLabelCount: donutAndBarAxisLabelCount,
    pdfIsDownloading: donutAndBarPdfIsDownloading,
    setPdfIsDownloading: setDonutAndBarPdfIsDownloading,
    localLocationFilter: donutAndBarLocalLocationFilter,
    resetLocalFilters: resetDonutAndBarHorizontalBarFilters,
  } = useIssueCategoriesByIntakeMethod({
    globalFilterSites,
    globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    globalFilterTimeframe,
    organisationData: chartOrganisationData,
  });

  const orgName = organisation?.name ?? 'your organisation';

  const donutAndBarHorizontalFilterData = useFormatPdfFilter(
    donutAndBarHorizontalBarFilters,
  );
  const substantiationChartFilterData = useFormatPdfFilter(
    substantiationChartLocalFilters,
  );
  const stackedBarChartFilterData = useFormatPdfFilter(
    stackedBarLocalFilterOptions,
  );

  const {
    downloadCaseTTR,
    downloadCategoriesByIntakeMethod,
    downloadSubstantiationData,
  } = useOperationalHealthDownloads(
    casesOverTime,
    categoriesByIntakeMethod,
    barHorizontalData,
  );

  const { width: barStackedWidth, height: barStackedHeight } =
    useElementDimensions(barStackedRef);
  const { width: barHorizontalWidth, height: barHorizontalHeight } =
    useElementDimensions(barHorizontalRef);
  const { width: donutAndBarWidth, height: donutAndBarHeight } =
    useElementDimensions(donutAndBarRef);

  const columnsMemo = useMemo(
    () => [{ label: 'Filter' }, { label: 'Selected' }],
    [],
  );
  const visibleColumnsMemo = useMemo(
    () => [{ label: 'Filter' }, { label: 'Selected' }],
    [],
  );

  if (isLoading) {
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );
  }

  const dashboardContent = (
    <>
      <HeroMetricContainer>
        {heroMetricData.map((metric) => (
          <HeroMetric
            key={metric.testId}
            data-testid={metric.testId}
            title={metric.title}
            tooltipText={metric.tooltip}
            metrics={metric.metrics}
            dashboard="operational"
            metric_id={metric.id}
          />
        ))}
      </HeroMetricContainer>
      <GraphPanel
        chartTitle="Cases time to resolution"
        chartTitleDescription="Distribution of cases (both open + closed) based on their progression from new to closed, and the total time it takes for the case to be resolved."
        headers={['Chart', 'Table']}
        dashboard="operational"
        chartId={CHART_IDS.case_time_to_resolution}
        pdfDownloadRef={barStackedRef}
        pdfDownloadWidth={Math.max(1000, barStackedWidth)}
        pdfDownloadHeight={barStackedHeight}
        togglePdfDownloading={setBarStackedChartPdfIsDownloading}
        panels={[
          <div
            ref={barStackedRef}
            key={`${CHART_IDS.case_time_to_resolution}-graph`}
          >
            <GraphContainer>
              <BarStackedContent
                isLoading={isBarStackedDataLoading}
                data={barStackedData}
              />
            </GraphContainer>
            <Legends
              items={timeToResolutionLegend}
              variant="dot"
              onClick={onStackedBarLegendClick}
              alwaysShowOverflow={barStackedChartPdfIsDownloading}
            />
            {barStackedChartPdfIsDownloading && (
              <Table
                columns={columnsMemo}
                visibleColumns={visibleColumnsMemo}
                tableData={
                  stackedBarChartFilterData as {
                    cellData: (string | number)[];
                    row: string;
                  }[]
                }
                emptyText=""
              />
            )}
          </div>,
          <GraphTableContainer
            key={`${CHART_IDS.case_time_to_resolution}-table`}
          >
            <PaginatedTable
              columns={barStackedTableColumns}
              visibleColumns={barStackedTableColumns}
              sortableColumns={barStackedTableColumns.map(labelExtractor)}
              tableData={barStackedTableData}
              sortedColumn={barStackedSortedColumn}
              onColumnSort={handleBarStackedColumnSort}
              maxRows={7}
              fixedRowHeight
              isFullWidth
              emptyText=""
            />
          </GraphTableContainer>,
        ]}
        onDownloadInsights={({ label }, print) =>
          downloadCaseTTR(label, stackedBarLocalFilterOptions, print)
        }
        onFiltersOpen={() => {
          setLocalFiltersOpen(true);
          setSelectedLocalFilters({
            options: stackedBarLocalFilterOptions,
            saveFunction: saveStackedBarLocalFilterOptions,
            resetOptions: resetStackedBarLocalFilterOptions,
            locationFilter: stackedBarLocalLocationFilter,
            originalSelectedLocationFilter: stackedBarLocalLocationFilter,
          });
          setFilteredChartId(CHART_IDS.case_time_to_resolution);
        }}
      />

      {barHorizontalData && (
        <GraphPanel
          chartTitle={`Substantiation status across ${orgName}`}
          chartTitleDescription="The accumulative number of reports in each substantiation status. This chart shows substantiation status of both active and closed cases."
          headers={['Chart', 'Table']}
          dashboard="operational"
          chartId={CHART_IDS.report_substantiation_totals}
          pdfDownloadRef={barHorizontalRef}
          pdfDownloadWidth={Math.max(1000, barHorizontalWidth)}
          pdfDownloadHeight={barHorizontalHeight}
          togglePdfDownloading={setSubstantiationChartPdfIsDownloading}
          panels={[
            <div
              ref={barHorizontalRef}
              key={`${CHART_IDS.report_substantiation_totals}-graph`}
            >
              <GraphContainer>
                <BarHorizontal
                  xTitle={barHorizontalXTitle ?? ''}
                  yTitle={barHorizontalYTitle ?? ''}
                  data={barHorizontalData ?? []}
                  graphId="substantiation-horizontal-graph"
                  tooltip={true}
                  tooltipType={'substantiation'}
                  titleDotColor={substantiationHoverBarColor}
                  onHover={onSubstantiationBarHover}
                />
              </GraphContainer>
              <Legends
                items={substantiationLegend}
                variant="dot"
                alwaysShowOverflow={substantiationChartPdfIsDownloading}
              />
              {substantiationChartPdfIsDownloading && (
                <Table
                  columns={columnsMemo}
                  visibleColumns={visibleColumnsMemo}
                  tableData={
                    substantiationChartFilterData as {
                      cellData: (string | number)[];
                      row: string;
                    }[]
                  }
                  emptyText=""
                />
              )}
            </div>,
            <PaginatedTable
              columns={barHorizontalTableColumns}
              visibleColumns={barHorizontalTableColumns}
              sortableColumns={barHorizontalTableColumns.map(labelExtractor)}
              tableData={barHorizontalTableData}
              sortedColumn={barHorizontalSortedColumn}
              onColumnSort={handlebarHorizontalColumnSort}
              maxRows={6}
              fixedRowHeight
              isFullWidth
              emptyText=""
              key={`${CHART_IDS.report_substantiation_totals}-table`}
            />,
          ]}
          onFiltersOpen={() => {
            setLocalFiltersOpen(true);
            setSelectedLocalFilters({
              options: substantiationChartLocalFilters,
              saveFunction: saveSubstantiationChartLocalFilters,
              resetOptions: resetSubstantiationChartLocalFilters,
              locationFilter: substantiationChartLocalLocationFilter,
              originalSelectedLocationFilter:
                substantiationChartLocalLocationFilter,
            });
            setFilteredChartId(CHART_IDS.report_substantiation_totals);
          }}
          onDownloadInsights={({ label }, print) =>
            downloadSubstantiationData(
              label,
              substantiationChartLocalFilters,
              print,
            )
          }
        />
      )}

      <GraphPanel
        chartTitle="Issue categories by intake method"
        chartTitleDescription="The total number of reports submitted via each intake method, subsequently broken down into their respective issue categories. The chart shows both active and closed cases."
        headers={['Chart', 'Table']}
        dashboard="operational"
        chartId={CHART_IDS.intake_methods_per_category}
        pdfDownloadRef={donutAndBarRef}
        pdfDownloadWidth={Math.max(1000, donutAndBarWidth)}
        pdfDownloadHeight={Math.max(
          200 + donutAndBarAxisLabelCount * 40,
          donutAndBarHeight,
        )}
        togglePdfDownloading={setDonutAndBarPdfIsDownloading}
        panels={[
          <div
            ref={donutAndBarRef}
            key={`${CHART_IDS.intake_methods_per_category}-graph`}
          >
            <DonutAndBarContent
              isLoading={donutAndBarHorizontalBarLoading}
              donutData={donutData}
              onDonutClick={onDonutClick}
              centerLabel={centerLabel}
              selectedSegment={selectedDonutSegment}
              barData={barData}
              barXTitle={barXTitle}
              barYTitle={barYTitle}
              barColor={barColor}
              xAxisMax={xAxisMax}
              pdfIsDownloading={donutAndBarPdfIsDownloading}
              categoryAxisLabelCount={donutAndBarAxisLabelCount}
            />
            <Legends
              items={donutLegend}
              variant={'dot'}
              onClick={onDonutLegendClick}
              alwaysShowOverflow={donutAndBarPdfIsDownloading}
            />
            {donutAndBarPdfIsDownloading && (
              <Table
                columns={columnsMemo}
                visibleColumns={visibleColumnsMemo}
                tableData={
                  donutAndBarHorizontalFilterData as {
                    cellData: (string | number)[];
                    row: string;
                  }[]
                }
                emptyText=""
              />
            )}
          </div>,
          <GraphTableContainer
            key={`${CHART_IDS.intake_methods_per_category}-table`}
          >
            <PaginatedTable
              columns={donutAndBarTableColumns}
              visibleColumns={donutAndBarTableColumns}
              sortableColumns={donutAndBarTableColumns.map(labelExtractor)}
              tableData={donutAndBarTableData}
              sortedColumn={donutAndBarSortedColumn}
              onColumnSort={handleDonutAndBarColumnSort}
              maxRows={7}
              fixedRowHeight
              isFullWidth
              emptyText=""
            />
          </GraphTableContainer>,
        ]}
        onFiltersOpen={() => {
          setLocalFiltersOpen(true);
          setSelectedLocalFilters({
            options: donutAndBarHorizontalBarFilters,
            saveFunction: saveDonutAndBarHorizontalBarFilters,
            resetOptions: resetDonutAndBarHorizontalBarFilters,
            locationFilter: donutAndBarLocalLocationFilter,
            originalSelectedLocationFilter: donutAndBarLocalLocationFilter,
          });
          setFilteredChartId(CHART_IDS.intake_methods_per_category);
        }}
        onDownloadInsights={({ label }, print) =>
          downloadCategoriesByIntakeMethod(
            label,
            donutAndBarHorizontalBarFilters,
            print,
          )
        }
      />

      {globalFilterOpen && (
        <GlobalFilter
          {...globalFilterProps}
          closeFilters={closeGlobalFilter}
          isOpen={globalFilterOpen}
          dashboard={InsightsDashboard.OPERATIONAL}
        />
      )}
      <LocalFilter
        isOpen={localFiltersOpen}
        filters={selectedLocalFilters.options}
        closeFilters={() => {
          setLocalFiltersOpen(false);
          setSelectedLocalFilters(defaultLocalFilter);
          setFilteredChartId(undefined);
        }}
        resetFilters={selectedLocalFilters.resetOptions}
        saveFilters={(filters) => {
          selectedLocalFilters.saveFunction(filters);
          setLocalFiltersOpen(false);
        }}
        dashboard={InsightsDashboard.OPERATIONAL}
        chartId={filteredChartId}
        selectedLocationFilter={selectedLocalFilters.locationFilter}
        setSelectedLocationFilter={(locationFilter) =>
          setSelectedLocalFilters({ ...selectedLocalFilters, locationFilter })
        }
      />

      <InsightsDisclaimer>
        <p>
          * All data in Integrity Intelligence is related to the named offender
          in the report
        </p>
      </InsightsDisclaimer>
    </>
  );

  return (
    <OperationalHealthDashboardStyled data-testid="operational-dashboard-container">
      <PageTitle>
        Ethics Operations in{' '}
        <ClickableTitle
          onClick={onLocationTitleClick}
          title={titleHoverDisplay}
        >
          {locationTitle}
        </ClickableTitle>{' '}
        for{' '}
        <ClickableTitle
          onClick={onTimeTitleClick}
          data-testid={'clickable-title-timeframe'}
        >
          {timeframeTitle}
        </ClickableTitle>
      </PageTitle>
      {dashboardContent}
    </OperationalHealthDashboardStyled>
  );
};
